import { Component, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

import { AuthService, EntityService, PageMetaService, SharedService, User } from './core';
import { BankingService } from './modules/banking/services';
import { ProfileService } from './modules/profile/services';
import { VoucherService } from './modules/voucher/services';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnDestroy {
  user: User;
  private subscriptionCheckInterval: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    public entityService: EntityService,
    private pageMetaService: PageMetaService,
    private authService: AuthService,
    private voucherService: VoucherService,
    private bankingService: BankingService,
    private sharedService: SharedService,
    private profileService: ProfileService,
    private swUpdate: SwUpdate
  ) {
    this.user = this.authService.user;
  }

  ngOnInit() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const route = this.getChild(this.route);

      route.data.subscribe(
        data => {
          this.titleService.setTitle('Purchase' + (data.title ? ' | ' + data.title : ''));
          this.pageMetaService.title.next(data.title);
          this.pageMetaService.backLink.next(data.backLink);
        }
      );
    });

    if (this.authService.isLoggedIn && this.user.isAdmin) {
      this.entityService.fetchCompanies();
    }

    if (this.authService.isLoggedIn && (this.authService.user?.isOwner || this.authService.user?.isOutlet)) {
      this.voucherService.getCompanyDetails().subscribe(res => {
        this.entityService.setCompanyDetails(res);
      });
    }

    this.getUserProfile();
    this.startSubscriptionCheck();
    this.getExploreProductStats();
    this.checkForAppUpdates();
  }

  /**
   * Prompts the user to reload when the service worker has fetched a new app version,
   * instead of silently continuing to serve the stale cached bundle.
   */
  checkForAppUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.pipe(
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY')
    ).subscribe(() => {
      if (confirm('A new version of this app is available. Reload now to get the latest updates?')) {
        window.location.reload();
      }
    });
  }

  startSubscriptionCheck(): void {
    this.getUserSubscriptionInfo();

    // Then repeat every hour (3600000 ms)
    this.subscriptionCheckInterval = setInterval(() => {
      this.getUserSubscriptionInfo();
    }, 3600000); // 1 hour in milliseconds
  }

  getExploreProductStats() {
    if (this.authService.isLoggedIn && !this.authService.user.isAdmin && !this.authService.user.isLeadAdmin) {
      this.authService.getExploreProductStats().subscribe({
        next: (response) => {
          if (response?.exploredProductModules) {
            const responseToSet = {
              exploredProductModules: response?.exploredProductModules,
              percentage: response.percentage
            }
            this.sharedService.setExploreProductStats(responseToSet);
          }
        },
        error: (error) => {
          this.sharedService.errorAlert(error.message);
        }
      });
    }
  }

  getChild(route: ActivatedRoute) {
    return route.firstChild ? this.getChild(route.firstChild) : route;
  }


  getUserProfile() {
    if (this.authService.user?.isOwner || this.authService.user?.isOutlet) {
      this.profileService.getUserProfileDetails().subscribe((res) => {
        if (res?.thumbnail) {
          this.sharedService.updateLoggedInUserThumbnail(res.thumbnail);
        }
      }, (error) => {
        this.sharedService.errorAlert(error.message);
      });
    }
  }

  /**
   * This method will fetch user's subscription info, will be called every hour to keep checking for expiry.
   */
  getUserSubscriptionInfo() {
    if ((this.authService?.user?.isOwner || this.authService.user?.isOutlet) && !this.authService.user?.isPaidUser) {
      this.authService.getUserSubscriptionInfo().subscribe({
        next: (response) => {
          this.authService.user.isPaidUser = response.isPaidUser;
          this.authService.user.freeTrialDays = response.freeTrialDays || 0;
          this.authService.user.invoiceCount = response.invoiceCount || 0;
        },
        error: (error) => {
          this.sharedService.errorAlert(error);
        }
      });
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.subscriptionCheckInterval);
  }
}